import React, { useState, useEffect } from 'react';
import { Inbox, Trash2, ExternalLink, Mail, RefreshCw, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const STATUSES = {
  new: 'New', needs_review: 'Needs Review', contacted: 'Contacted', approved: 'Approved',
  declined: 'Declined', account_created: 'Account Created', embed_sent: 'Embed Sent',
};
const STATUS_STYLES = {
  new: 'bg-blue-50 text-blue-800 border-blue-200',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-200',
  contacted: 'bg-slate-100 text-slate-700 border-slate-300',
  approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
  account_created: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  embed_sent: 'bg-teal-50 text-teal-800 border-teal-200',
};
const CATEGORY_LABELS = {
  ward_access: 'Ward access',
  citywide_access: 'Mayor / Citywide access',
  supporter_only: 'Supporter only',
};
const CATEGORY_STYLES = {
  ward_access: 'bg-blue-50 text-blue-800 border-blue-300',
  citywide_access: 'bg-indigo-50 text-indigo-800 border-indigo-300',
  supporter_only: 'bg-[#C9A84C]/15 text-[#7A5C12] border-[#C9A84C]',
};
const LEGACY_TYPE_LABELS = {
  embed: 'Embed', member_access: 'Member access', followup_access: 'Follow-up access',
  ward_access: 'Ward access', citywide_access: 'Citywide access', question: 'Question',
};
const NOTIF_STYLES = {
  sent: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  queued: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

function RequestsTab() {
  const [items, setItems] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const load = () => api.get('/admin/pita/requests').then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (item, status) => {
    try {
      await api.put(`/admin/pita/requests/${item.id}`, { status });
      toast.success(`Marked as ${STATUSES[status]}`); load();
    } catch { toast.error('Update failed'); }
  };
  const saveNotes = async (item) => {
    try {
      await api.put(`/admin/pita/requests/${item.id}`, { admin_notes: notesDraft[item.id] ?? item.admin_notes ?? '' });
      toast.success('Notes saved'); load();
    } catch { toast.error('Save failed'); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete request from ${item.name}?`)) return;
    await api.delete(`/admin/pita/requests/${item.id}`); toast.success('Deleted'); load();
  };

  if (items === null) return <p className="text-sm text-slate-400 py-6">Loading…</p>;
  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 leading-relaxed" data-testid="pita-requests-note">
        PITA access requests are <strong>private</strong>. Approving here never creates an account, activates an embed,
        grants report access, or emails the requester automatically. Account creation (via PITA Network members) and
        embed delivery are separate manual admin steps, then mark the request Account Created or Embed Sent.
        <strong> Supporter-only requests never include private report access.</strong>
      </div>
      {items.length === 0 && <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="pita-requests-empty">No PITA access requests yet. Requests from /pita/request-access appear here.</CardContent></Card>}
      {items.map((r) => (
        <Card key={r.id} data-testid={`pita-request-${r.id}`}>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-bold text-[#1a2744]">{r.name}</p>
                  <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_STYLES[r.status] || ''}`}>{STATUSES[r.status] || r.status}</Badge>
                  {r.request_type && (
                    <Badge variant="outline" className={`text-[10px] font-bold ${CATEGORY_STYLES[r.request_type] || 'border-slate-300 text-slate-600'}`} data-testid={`pita-request-category-${r.id}`}>
                      {CATEGORY_LABELS[r.request_type] || r.request_type}
                    </Badge>
                  )}
                  {r.ward && <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">Ward {r.ward}</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1">{r.email}{r.phone ? ` · ${r.phone}` : ''}{r.organization ? ` · ${r.organization}` : ''}{r.role ? ` · ${r.role}` : ''} · {(r.created_at || '').slice(0, 10)}</p>
                {r.request_type && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`pita-request-badges-${r.id}`}>
                    {r.request_type === 'supporter_only' ? (
                      <>
                        <Badge variant="outline" className="text-[10px] font-bold bg-[#C9A84C]/15 text-[#7A5C12] border-[#C9A84C]">Supporter only</Badge>
                        <Badge variant="outline" className="text-[10px] font-bold bg-slate-100 text-slate-600 border-slate-300">No private report access</Badge>
                      </>
                    ) : (r.wants_followup || r.wants_citywide_visibility) ? (
                      <Badge variant="outline" className="text-[10px] font-bold bg-amber-50 text-amber-800 border-amber-300">Reports access requested</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] font-bold bg-slate-100 text-slate-600 border-slate-300">No reports access</Badge>
                    )}
                    {(r.wants_embed || (r.website_url && r.request_type !== 'supporter_only')) && (
                      <Badge variant="outline" className="text-[10px] font-bold bg-teal-50 text-teal-800 border-teal-300">Embed requested</Badge>
                    )}
                    {r.wants_citywide_visibility && (
                      <Badge variant="outline" className="text-[10px] font-bold bg-indigo-50 text-indigo-800 border-indigo-300">Citywide requested</Badge>
                    )}
                  </div>
                )}
                {!r.request_type && (r.request_types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.request_types.map((t) => <Badge key={t} variant="outline" className="text-[10px] border-[#C9A84C] text-[#7A5C12] bg-[#C9A84C]/10 font-bold">{LEGACY_TYPE_LABELS[t] || t}</Badge>)}
                  </div>
                )}
                {r.message && <p className="text-sm text-slate-600 mt-2 leading-relaxed border-l-2 border-slate-200 pl-3">{r.message}</p>}
                {r.website_url && (
                  <a href={r.website_url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center gap-1 mt-2"><ExternalLink className="h-3 w-3" /> Website</a>
                )}
                <div className="mt-3">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Private admin notes</label>
                  <Textarea rows={2} value={notesDraft[r.id] ?? r.admin_notes ?? ''} onChange={(e) => setNotesDraft((d) => ({ ...d, [r.id]: e.target.value }))} className="text-sm" data-testid={`pita-request-notes-${r.id}`} />
                  <Button size="sm" variant="outline" onClick={() => saveNotes(r)} className="h-8 text-xs mt-1.5 font-bold" data-testid={`pita-request-save-notes-${r.id}`}>Save Notes</Button>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0 w-full lg:w-52">
                <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                  <SelectTrigger className="h-9 text-xs" data-testid={`pita-request-status-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => remove(r)} className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`pita-request-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function NotificationsTab() {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState('');
  const load = () => api.get('/admin/pita/notifications').then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const retry = async (item) => {
    setBusyId(item.id);
    try {
      const res = await api.post(`/admin/pita/notifications/${item.id}/send`);
      if (res.data.ok) toast.success('Notification sent'); else toast.error(res.data.error || 'Send failed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setBusyId('');
  };

  if (items === null) return <p className="text-sm text-slate-400 py-6">Loading…</p>;
  return (
    <div className="space-y-3">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 leading-relaxed" data-testid="pita-notifications-note">
        Every PITA submission triggers one notification to the configured admin address (see PITA Settings).
        If SMTP is unavailable the notification is <strong>queued here</strong> and can be retried manually.
        Requesters and supporters are never emailed automatically.
      </div>
      {items.length === 0 && <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="pita-notifications-empty">No notifications logged yet.</CardContent></Card>}
      {items.map((n) => (
        <Card key={n.id} data-testid={`pita-notification-${n.id}`}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400 shrink-0" />
                <p className="font-bold text-[#1a2744] text-sm truncate">{n.subject}</p>
                <Badge variant="outline" className={`text-[10px] font-bold ${NOTIF_STYLES[n.status] || ''}`}>{(n.status || '').toUpperCase()}</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">To: {n.to_email} · {(n.created_at || '').slice(0, 16).replace('T', ' ')}{n.sent_at ? ` · Sent ${n.sent_at.slice(0, 16).replace('T', ' ')}` : ''}</p>
              {n.error && <p className="text-xs text-amber-700 mt-1">{n.error}</p>}
            </div>
            {n.status !== 'sent' && (
              <Button size="sm" variant="outline" disabled={busyId === n.id} onClick={() => retry(n)} className="h-9 text-xs font-bold shrink-0" data-testid={`pita-notification-retry-${n.id}`}>
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busyId === n.id ? 'animate-spin' : ''}`} /> Retry Send
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AccessLogTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/admin/pita/access-log').then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  if (items === null) return <p className="text-sm text-slate-400 py-6">Loading…</p>;
  return (
    <div className="space-y-2">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-600 leading-relaxed" data-testid="pita-access-log-note">
        Privacy-safe log of admin actions on PITA records: views, status changes, settings updates, and deletions.
      </div>
      {items.length === 0 && <Card><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="pita-access-log-empty">No PITA activity logged yet.</CardContent></Card>}
      {items.map((l) => (
        <div key={l.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600 border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid={`pita-log-${l.id}`}>
          <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-bold text-[#1a2744]">{l.user_email}</span>
          <span>{(l.action || '').replace(/_/g, ' ')}</span>
          <span className="text-slate-400">{l.entity}{l.detail ? ` · ${l.detail}` : ''}</span>
          <span className="ml-auto text-slate-400">{(l.created_at || '').slice(0, 16).replace('T', ' ')}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminPitaRequests() {
  return (
    <div data-testid="admin-pita-requests-page">
      <div className="flex items-center gap-3 mb-1">
        <Inbox className="h-6 w-6 text-[#1a2744]" />
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">PITA Network Requests</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Embed, member, and follow-up access requests for the Public Issue Tracker App (PITA). Private, approval-based, no automatic accounts or emails.</p>
      <Tabs defaultValue="requests">
        <TabsList className="mb-4">
          <TabsTrigger value="requests" data-testid="pita-tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="pita-tab-notifications">Notification Log</TabsTrigger>
          <TabsTrigger value="log" data-testid="pita-tab-access-log">Access Log</TabsTrigger>
        </TabsList>
        <TabsContent value="requests"><RequestsTab /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab /></TabsContent>
        <TabsContent value="log"><AccessLogTab /></TabsContent>
      </Tabs>
    </div>
  );
}
